import { Injectable, SecurityContext, OnDestroy } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { CommsService } from '../comms/comms.service';
import { LocalInfoService } from '../local-info/local-info.service';
import { LoggerService } from '../logger/logger.service';
import { VantageShellService } from '../vantage-shell/vantage-shell.service';
import { throwError, Subscription } from 'rxjs';

interface RequestParam {
	cmsOption: string;
	api: string;
	apiParam: string;
	observableMsg: string;
	applyDeviceFilter: boolean;
	responseOrigin?: boolean;
}

@Injectable({
	providedIn: 'root'
})
export class CMSService implements OnDestroy {
	language: string;
	region: string;
	segment: string; // VAN-5872, server switch feature
	localInfo: any;
	defaultInfo = { Lang: 'en', GEO: 'us', OEM: 'Lenovo', OS: 'Windows', Segment: 'Consumer', Brand: 'Lenovo' };
	fetchRequestMap = {};
	commonNotificationSubscription: Subscription;

	constructor(
		private commsService: CommsService,
		private vantageShellService: VantageShellService,
		private localInfoService: LocalInfoService,
		private logger: LoggerService,
	) {
		localInfoService.getLocalInfo().then(result => {
			this.localInfo = result;
		}).catch(e => { });
	}

	async deviceFilter(filters) {
		if (!filters) {
			return true;
		}

		try {
			return await this.vantageShellService.deviceFilter(filters);
		} catch  {
			return false;
		}
	}

	private updateServerSwitchCMSOptions(defaults, queryParams) {
		const cmsOption = Object.assign(defaults, queryParams);
		return cmsOption;

	}

	private async filterCMSContent(contents) {
		const promises = [];
		contents.forEach((content) => {
			promises.push(this.deviceFilter(content.Filters));
		});

		return Promise.all(promises).then((deviceFilterValues) => {
			return contents.filter((content, index) => {
				return deviceFilterValues[index];
			});
		});
	}

	private async getLocalinfo(): Promise<any> {
		if (!this.localInfo) {
			try {
				this.localInfo = await this.localInfoService.getLocalInfo();
			} catch (error) {
				this.logger.error('CMSService.getLocalinfo', error.message);
			}
		}

		return this.localInfo || this.defaultInfo;
	}

	public async fetchCMSEntitledAppList(queryParams) {
		const locInfo = await this.getLocalinfo();
		const defaults = {
			Lang: locInfo.Lang,
			GEO: locInfo.GEO
		};
		const cmsOption = this.updateServerSwitchCMSOptions(defaults, queryParams);
		return this.postRequest({
			cmsOption,
			api: '/api/v1/entitledapps',
			apiParam: null,
			observableMsg: 'fetchCMSEntitledAppList error',
			applyDeviceFilter: false
		});
	}

	public async fetchCMSAppDetails(appId, queryParams) {
		const locInfo = await this.getLocalinfo();
		const defaults = {
			Lang: locInfo.Lang
		};
		const cmsOption = this.updateServerSwitchCMSOptions(defaults, queryParams);
		return this.postRequest({
			cmsOption,
			api: '/api/v1/apps/',
			apiParam: appId,
			observableMsg: 'fetchCMSAppDetails error',
			applyDeviceFilter: false
		});
	}

	public async postRequest(param: RequestParam) {
		const {
			cmsOption,
			api,
			apiParam,
			observableMsg,
			applyDeviceFilter, responseOrigin } = param;

		let results;
		try {

			const composeApi = apiParam ? api + apiParam : api;
			const response: any = await this.commsService.endpointGetCall(composeApi, cmsOption, {}).toPromise();

			if (applyDeviceFilter) {
				results = await this.filterCMSContent(response.Results);

			} else {
				results = responseOrigin ? response : response.Results;
			}
			return results;
		} catch (ex) {
			if (observableMsg) {
				throwError(new Error(observableMsg));
			} else {
				throw (ex);
			}
		} finally {
		}
	}

	ngOnDestroy() {
		if (this.commonNotificationSubscription) {
			this.commonNotificationSubscription.unsubscribe();
		}
	}
}
